"use client";

import {
	applyPersonaToQuery,
	personaDisplayLabel,
	renderPersonaContext,
} from "@repo/core/client";
import { PersonaRoleIcon } from "@ui/components/persona-role-icon";
import { MessageSquareIcon } from "lucide-react";
import { PERSONAS_BAND_EXAMPLES } from "../lib/copy";

/**
 * The Personas band's examples: one prompt, asked as two different people,
 * for a local business (two people choosing a pharmacy) beside a software
 * category (two people at one company). In each, the question text is
 * identical in both rows and set in the foreground color; the persona line
 * in front of it is muted, which is the whole idea in one picture: only the
 * asker changes. The lines are rendered by the same `renderPersonaContext` /
 * `applyPersonaToQuery` the product sends.
 */
export function SameQuestionDifferentPeople() {
	return (
		<div className="min-w-0 space-y-5">
			{PERSONAS_BAND_EXAMPLES.map((example) => (
				<div key={example.key} className="min-w-0 space-y-2">
					<p className="text-xs font-semibold text-muted-foreground">
						{example.audience}
					</p>
					<div className="flex items-start gap-2 rounded-lg border bg-background p-3">
						<MessageSquareIcon
							className="mt-0.5 size-4 shrink-0 text-muted-foreground"
							aria-hidden
						/>
						<div className="min-w-0">
							<p className="text-xs text-muted-foreground">
								Your prompt
							</p>
							<p className="text-sm font-medium">
								{example.query}
							</p>
						</div>
					</div>
					{example.personas.map((persona) => {
						const combined = applyPersonaToQuery(
							renderPersonaContext(persona),
							example.query,
						);
						return (
							<div
								key={persona.key}
								className="space-y-2 rounded-lg border bg-background p-3"
							>
								<div className="flex items-center gap-2">
									<PersonaRoleIcon
										roleGroup={persona.roleGroup}
										askerType={persona.askerType}
										household={persona.household}
										size="sm"
									/>
									<p className="min-w-0 truncate text-sm">
										<span className="text-muted-foreground">
											Asked as{" "}
										</span>
										<span className="font-semibold">
											{personaDisplayLabel(persona)}
										</span>
									</p>
								</div>
								<p className="text-xs leading-relaxed">
									<span className="line-clamp-2 text-muted-foreground sm:line-clamp-none">
										{combined.text.slice(
											0,
											combined.queryStart,
										)}
									</span>
									<span className="mt-1 block font-medium text-foreground">
										{combined.text.slice(
											combined.queryStart,
										)}
									</span>
								</p>
							</div>
						);
					})}
				</div>
			))}
		</div>
	);
}
